import { isAndroidBrowser, isIOS, isIOSBrowser } from '@ab-core/functions/isShopApp';
import type { CustomerDocument } from '@ab-core/graphql/dist';
import { logger } from '@ab-core/logger';

const FALLBACK_PDF_FILENAME = 'document.pdf';
const B64_CONTENT_TYPE_PDF = 'application/pdf';
const URL_REVOKE_DELAY = 1000;

export const downloadPdfFile = (customerDocument: CustomerDocument): void => {
    if (!customerDocument?.binData) {
        return;
    }

    const isIOSDevice = isIOS();
    const isIOSBrowserEnvironment = isIOSBrowser();
    const isAndroidBrowserEnvironment = isAndroidBrowser();

    if (isIOSBrowserEnvironment) {
        downloadFileForIOSBrowser(customerDocument);
    } else if (isIOSDevice) {
        downloadFileForIOSApp(customerDocument);
    } else if (isAndroidBrowserEnvironment) {
        downloadFileForAndroidBrowser(customerDocument);
    } else {
        downloadFileForAndroidApp(customerDocument);
    }
};

const b64toBlob = (b64Data = '', contentType = '', sliceSize = 512) => {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];
    const isIOSDevice = isIOS();

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);

        const byteNumbers = [];

        for (let i = 0; i < slice.length; i++) {
            byteNumbers.push(slice.charCodeAt(i));
        }

        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
    }

    if (isIOSDevice) {
        return new Blob(byteArrays, {
            type: contentType,
            endings: 'native'
        });
    } else {
        return new Blob(byteArrays, { type: contentType });
    }
};

const downloadFileForIOSApp = (customerDocument: CustomerDocument) => {
    const octetStreamBlob = b64toBlob(customerDocument.binData, 'application/octet-stream', 512);

    if (typeof window === 'undefined') {
        return;
    }

    try {
        if (
            navigator.share &&
            navigator.canShare &&
            navigator.canShare({
                files: [
                    new File([octetStreamBlob], customerDocument?.fileName || FALLBACK_PDF_FILENAME, {
                        type: octetStreamBlob.type
                    })
                ]
            })
        ) {
            const file = new File([octetStreamBlob], customerDocument?.fileName || FALLBACK_PDF_FILENAME, {
                type: octetStreamBlob.type
            });
            navigator.share({ files: [file] });

            return;
        }
    } catch (shareError) {
        logger.error({
            title: 'K1 iOS navigator.share failed',
            infos: { error: shareError.message, fileName: customerDocument.fileName }
        });
    }

    const url = URL.createObjectURL(octetStreamBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = customerDocument.fileName || FALLBACK_PDF_FILENAME;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const downloadFileForAndroidApp = (customerDocument: CustomerDocument) => {
    const pdfBlob = b64toBlob(customerDocument?.binData, B64_CONTENT_TYPE_PDF, 512);
    const fileName = customerDocument?.fileName || FALLBACK_PDF_FILENAME;

    const url = URL.createObjectURL(pdfBlob);
    const anchor = document.createElement('a');

    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);

    revokeObjectURL(url, URL_REVOKE_DELAY);
};

const revokeObjectURL = (url: string, delay = 500): void => {
    setTimeout(() => {
        URL.revokeObjectURL(url);
    }, delay);
};

const downloadFileForIOSBrowser = (customerDocument: CustomerDocument) => {
    try {
        const dataUrl = `data:application/pdf;base64,${customerDocument.binData}`;
        const newWindow = window.open(dataUrl, '_blank');

        if (newWindow) {
            return;
        }
    } catch (error) {
        logger.error({
            title: 'iOS Browser: Data URL method failed',
            infos: { error: error.message, fileName: customerDocument.fileName }
        });
    }

    const pdfBlob = b64toBlob(customerDocument?.binData, B64_CONTENT_TYPE_PDF, 512);
    const url = URL.createObjectURL(pdfBlob);
    const anchor = document.createElement('a');

    anchor.href = url;
    anchor.download = customerDocument?.fileName || FALLBACK_PDF_FILENAME;
    anchor.target = '_blank';

    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);

    revokeObjectURL(url, URL_REVOKE_DELAY);
};

const downloadFileForAndroidBrowser = (customerDocument: CustomerDocument) => {
    const pdfBlob = b64toBlob(customerDocument?.binData, B64_CONTENT_TYPE_PDF, 512);
    const fileName = customerDocument?.fileName || FALLBACK_PDF_FILENAME;
    const url = URL.createObjectURL(pdfBlob);
    const anchor = document.createElement('a');

    anchor.href = url;
    anchor.download = fileName;

    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);

    revokeObjectURL(url, URL_REVOKE_DELAY);
};
